import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useExitAnim } from "../../hooks/useExitAnim";
import { supabase } from "../../lib/supabase";
import { useConversationResync } from "../../hooks/useConversationResync";
import { getMessagesCache, setMessagesCache } from "../../lib/setterPerformanceCacheService";
import ChatBubble from "./ChatBubble";
import { useGhlUserNameMap } from "../../hooks/useGhlUserNameMap";
import { ReplyTimeline } from "./ReplyTimeline";
import { CopyConversationButton } from "./CopyConversationButton";
import { PreviewCard } from "./PreviewCard";
import { serializeConversation } from "../../utils/conversationToText";
import { DataContext } from "../../context/DataContext";
import {
  useNeighborPrefetch,
  usePreviewData,
  invalidatePreviewBundle,
  type NavSibling,
} from "../../hooks/data-fetching/useConversationPreviews";

// =============================================================================
// CONVERSATION VIEW MODAL
// =============================================================================
// PURPOSE: Display a single conversation's messages in Instagram DM-style chat bubbles
// with a synchronized Reply Timeline sidebar.
//
// LAYOUT: Split panel — left (Reply Timeline, ~250px) | right (messages, flex-1)
// Collapses to single column below lg breakpoint.
//
// FEATURES:
// - Chat bubble UI (lead=gray/left, setter=blue/right)
// - Reply Timeline sidebar with turn stats, speed annotations, day separators
// - Synchronized scrolling between timeline and messages
// - Resync button to fetch ALL messages from GHL if some are missing
//
// DATA SOURCE:
// - ghl_messages table filtered by conversation_id
// - ghl_conversations for reply_turns, ghl_contact_id, setter assignment
// =============================================================================

interface Message {
  id: string;
  message_text: string | null;
  sent_at: string;
  sender_type: "lead" | "setter" | string;
  has_media?: boolean;
  ghl_sender_user_id?: string | null;
  owner_at_send?: string | null;
}

interface ConversationViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: string | null;
  contactName: string;
  leadInstagram?: string | null;
  timezone?: string;
  organizationId?: string;
  highlightTimestamp?: string;
  anchorTimestamp?: string;
  onDismiss?: (conversationId: string) => void;
  /** When set, messages outside this range are dimmed (grayed out) */
  activeDateRange?: { start: Date; end: Date } | null;
  // ---------------------------------------------------------------------------
  // F2 (dm-swipe-nav) — optional CONTROLLED neighbor-nav props. The PARENT owns
  // the array + index; CVM never mutates them, it only calls onNavigate(next).
  // Nav is enabled ONLY when siblings && currentIndex != null && onNavigate &&
  // siblings.length > 1 — so every existing call site keeps rendering as today.
  // ---------------------------------------------------------------------------
  siblings?: NavSibling[];
  currentIndex?: number;
  onNavigate?: (nextIndex: number) => void;
  onNeedMore?: () => void;
  siblingsHasMore?: boolean;
  siblingsTotal?: number;
}

// Find the closest message to a timestamp (within 60 seconds tolerance)
function findClosestMessageIdx(messages: Message[], timestamp: string | undefined): number {
  if (!timestamp || messages.length === 0) return -1;
  const targetMs = new Date(timestamp).getTime();
  let bestIdx = -1;
  let bestDiff = Infinity;
  for (let i = 0; i < messages.length; i++) {
    const diff = Math.abs(new Date(messages[i].sent_at).getTime() - targetMs);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIdx = i;
    }
  }
  // Only match if within 60 seconds
  return bestDiff <= 60_000 ? bestIdx : -1;
}

function getHighlightType(
  msg: Message,
  highlightTs: string | undefined,
  anchorTs: string | undefined,
  messages: Message[]
): "highlight" | "anchor" | undefined {
  if (!highlightTs) return undefined;
  const highlightIdx = findClosestMessageIdx(messages, highlightTs);
  const anchorIdx = findClosestMessageIdx(messages, anchorTs);
  const msgIdx = messages.indexOf(msg);
  if (msgIdx === highlightIdx) return "highlight";
  if (msgIdx === anchorIdx) return "anchor";
  return undefined;
}

export const ConversationViewModal: React.FC<ConversationViewModalProps> = ({
  isOpen,
  onClose,
  conversationId,
  contactName,
  leadInstagram,
  timezone = "UTC",
  organizationId,
  highlightTimestamp,
  anchorTimestamp,
  onDismiss,
  activeDateRange,
  siblings,
  currentIndex,
  onNavigate,
  onNeedMore,
  siblingsHasMore,
  siblingsTotal,
}) => {
  const dataCtx = React.useContext(DataContext);
  const orgName = dataCtx?.currentOrganization?.name;

  // ---------------------------------------------------------------------------
  // F2 — nav enable guard + the ACTIVE conversation. When nav is ON the active
  // conversationId/contactName/leadInstagram come from siblings[currentIndex];
  // when OFF the standalone props are authoritative and everything below compiles
  // untouched. Empty array is truthy and siblings[i] on [] is undefined, so the
  // length > 1 gate is what actually turns the chrome on.
  // ---------------------------------------------------------------------------
  const navEnabled = !!(
    siblings &&
    currentIndex != null &&
    onNavigate &&
    siblings.length > 1 &&
    siblings[currentIndex]
  );
  const activeSibling = navEnabled ? siblings![currentIndex!] : null;
  const effConversationId = activeSibling ? activeSibling.conversationId : conversationId;
  const effContactName = activeSibling ? activeSibling.contactName : contactName;
  const effLeadInstagram = activeSibling
    ? (activeSibling.leadInstagram ?? null)
    : (leadInstagram ?? null);

  // Deep-link (highlight/anchor/activeDateRange) applies ONLY to the conversation
  // the modal was OPENED on. A neighbor was not opened via a date deep-link, so
  // carrying it would grey the new thread (:dimming) and hijack the scroll anchor.
  // openConvIdRef captures the first active id at open; once we navigate away the
  // deep-link is suppressed (F3 reset). Reading a ref in render is safe here — no
  // mutation during render.
  const openConvIdRef = useRef<string | null>(null);
  const atOpenConversation =
    !navEnabled || openConvIdRef.current === null || effConversationId === openConvIdRef.current;
  const effHighlightTimestamp = atOpenConversation ? highlightTimestamp : undefined;
  const effAnchorTimestamp = atOpenConversation ? anchorTimestamp : undefined;
  const effActiveDateRange = atOpenConversation ? activeDateRange : null;

  // Keep the modal mounted through the 200ms exit so anim-modal-out plays.
  const { mounted, closing } = useExitAnim(isOpen, 200);

  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cursor for "Load older messages" — created_at of the oldest currently loaded message
  const oldestCursorRef = useRef<string | null>(null);
  const [resyncSuccess, setResyncSuccess] = useState<string | null>(null);
  const [setterName, setSetterName] = useState<string | null>(null);
  const [ghlContactId, setGhlContactId] = useState<string | null>(null);
  const [ghlConversationId, setGhlConversationId] = useState<string | null>(null);
  const [ghlLocationId, setGhlLocationId] = useState<string | null>(null);
  const [replyTurns, setReplyTurns] = useState<number | null>(null);
  const [assignedUserId, setAssignedUserId] = useState<string | null>(null);
  // GHL user id → display name, for the owner_at_send bubble badge (STEP 0)
  const ghlUserNames = useGhlUserNameMap(organizationId);

  // Reply Timeline overlay toggle — below lg: the sidebar (line ~613) is hidden
  // with no substitute, so this shows the same <ReplyTimeline> full-width as an
  // in-modal overlay instead (Mako 2026-07-13 responsive pass).
  const [showTimelineOverlay, setShowTimelineOverlay] = useState(false);

  // Synchronized scrolling + click-to-highlight state
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null);
  const [clickHighlightId, setClickHighlightId] = useState<string | null>(null);
  const clickHighlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSyncScrolling = useRef(false);
  const initialScrollDone = useRef(false);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Refs for scroll-to-highlight / scroll-to-date-range
  const highlightRef = useRef<HTMLDivElement>(null);
  const firstInRangeRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);

  // Resync hook
  const { resync, isResyncing, error: resyncError } = useConversationResync();

  // Set ref for a message bubble (for click-to-scroll from timeline)
  const setMessageRef = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) messageRefs.current.set(id, el);
    else messageRefs.current.delete(id);
  }, []);

  // Page size for initial and "load older" fetches.
  // Capped at 200 to prevent silent truncation at PGRST_MAX_ROWS=1000 (Phase 7.4).
  // Fetch newest-first (DESC), then reverse for display (oldest at top).
  const MSG_PAGE_SIZE = 200;

  // Fetch messages from Supabase (stale-while-revalidate)
  const fetchMessages = useCallback(async () => {
    if (!effConversationId || !supabase) {
      setMessages([]);
      return;
    }

    // Stale-while-revalidate: show cached messages instantly
    const cached = organizationId
      ? getMessagesCache<Message[]>(effConversationId, organizationId)
      : null;
    if (cached) {
      setMessages(cached);
      oldestCursorRef.current = cached.length > 0 ? cached[0].sent_at : null;
      setHasOlderMessages(cached.length >= MSG_PAGE_SIZE);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      // Fetch newest first (DESC) with hard cap at MSG_PAGE_SIZE.
      // Prevents OOM and PGRST_MAX_ROWS silent truncation on long conversations.
      const { data, error: queryError } = await supabase!
        .from("ghl_messages")
        .select(
          "id, message_text, sent_at, sender_type, has_media, ghl_sender_user_id, sender_name, message_type, owner_at_send"
        )
        .eq("conversation_id", effConversationId)
        .not("message_type", "in", "(TYPE_ACTIVITY_OPPORTUNITY,TYPE_INTERNAL_COMMENT,TYPE_CALL)")
        .order("sent_at", { ascending: false })
        .limit(MSG_PAGE_SIZE);

      if (queryError) throw queryError;

      // Filter out system messages (Opportunity created/updated/deleted)
      const filtered = (data || []).filter(
        (m) =>
          !m.message_text?.includes("Opportunity created") &&
          !m.message_text?.includes("Opportunity updated") &&
          !m.message_text?.includes("Opportunity deleted")
      );

      // Reverse to display oldest-at-top, newest-at-bottom
      const result = (filtered as Message[]).reverse();

      if (organizationId) {
        setMessagesCache(effConversationId, organizationId, result);
      }

      // Track oldest message cursor for "Load older" pagination
      oldestCursorRef.current = result.length > 0 ? result[0].sent_at : null;
      setHasOlderMessages(filtered.length >= MSG_PAGE_SIZE);
      setMessages(result);
    } catch (err) {
      if (!cached) {
        console.warn("Failed to fetch messages:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch messages");
      }
    } finally {
      setLoading(false);
    }
  }, [effConversationId, organizationId]);

  // Load older messages — cursor pagination using sent_at of oldest currently loaded message
  const loadOlderMessages = useCallback(async () => {
    if (!effConversationId || !supabase || !oldestCursorRef.current || loadingOlder) return;

    setLoadingOlder(true);

    try {
      const { data, error: queryError } = await supabase!
        .from("ghl_messages")
        .select(
          "id, message_text, sent_at, sender_type, has_media, ghl_sender_user_id, sender_name, message_type, owner_at_send"
        )
        .eq("conversation_id", effConversationId)
        .not("message_type", "in", "(TYPE_ACTIVITY_OPPORTUNITY,TYPE_INTERNAL_COMMENT,TYPE_CALL)")
        .lt("sent_at", oldestCursorRef.current)
        .order("sent_at", { ascending: false })
        .limit(MSG_PAGE_SIZE);

      if (queryError) throw queryError;

      const filtered = (data || []).filter(
        (m) =>
          !m.message_text?.includes("Opportunity created") &&
          !m.message_text?.includes("Opportunity updated") &&
          !m.message_text?.includes("Opportunity deleted")
      );

      // Reverse so oldest is at top when prepended
      const older = (filtered as Message[]).reverse();

      if (older.length > 0) {
        oldestCursorRef.current = older[0].sent_at;
        setMessages((prev) => [...older, ...prev]);
      }

      setHasOlderMessages(filtered.length >= MSG_PAGE_SIZE);
    } catch (err) {
      console.warn("Failed to load older messages:", err);
    } finally {
      setLoadingOlder(false);
    }
  }, [effConversationId, loadingOlder]);

  // Fetch conversation metadata (setter name, ghl_contact_id, reply_turns)
  useEffect(() => {
    if (!isOpen || !effConversationId || !supabase) {
      setSetterName(null);
      setGhlContactId(null);
      setGhlConversationId(null);
      setGhlLocationId(null);
      setReplyTurns(null);
      setAssignedUserId(null);
      return;
    }
    // Fetch GHL location ID for deep-link
    if (organizationId) {
      supabase
        .from("organizations")
        .select("ghl_location_id")
        .eq("id", organizationId)
        .single()
        .then(({ data }) => {
          if (data?.ghl_location_id) setGhlLocationId(data.ghl_location_id);
        });
    }
    Promise.resolve(
      supabase
        .from("ghl_conversations")
        .select("ghl_assigned_user_id, ghl_contact_id, ghl_conversation_id, reply_turns")
        .eq("id", effConversationId)
        .single()
        .then(({ data }) => {
          if (data?.ghl_contact_id) setGhlContactId(data.ghl_contact_id);
          if (data?.ghl_conversation_id) setGhlConversationId(data.ghl_conversation_id);
          if (data?.reply_turns != null) setReplyTurns(data.reply_turns);
          setAssignedUserId(data?.ghl_assigned_user_id ?? null);
          if (!data?.ghl_assigned_user_id) return;
          // TODO: migrate to useProfiles() — Phase 7.2 (docs/PLAN_rbac_security_scale_hardening.md)
          return supabase!
            .from("profiles")
            .select("full_name")
            .eq("ghl_user_id", data.ghl_assigned_user_id)
            .single();
        })
        .then((result) => {
          if (result?.data?.full_name) setSetterName(result.data.full_name);
        })
    ).catch(() => {
      // Setter lookup is non-critical — don't break the modal
    });
  }, [isOpen, effConversationId]);

  // Fetch messages when modal opens
  useEffect(() => {
    if (!isOpen || !effConversationId) {
      setMessages([]);
      setResyncSuccess(null);
      setHasOlderMessages(false);
      oldestCursorRef.current = null;
      hasScrolledRef.current = false;
      initialScrollDone.current = false;
      setActiveMessageId(null);
      setShowTimelineOverlay(false);
      return;
    }
    hasScrolledRef.current = false;
    initialScrollDone.current = false;
    fetchMessages();
  }, [isOpen, effConversationId, fetchMessages]);

  // Auto-scroll to highlighted message or first in-range message after load
  useEffect(() => {
    if (!loading && messages.length > 0 && !hasScrolledRef.current) {
      // Priority: highlight timestamp > first message in active date range > bottom
      const scrollTarget = effHighlightTimestamp
        ? highlightRef.current
        : effActiveDateRange
          ? firstInRangeRef.current
          : null;
      if (scrollTarget) {
        hasScrolledRef.current = true;
        requestAnimationFrame(() => {
          scrollTarget.scrollIntoView({ behavior: "smooth", block: "center" });
          // Mark initial scroll done after animation
          setTimeout(() => {
            initialScrollDone.current = true;
          }, 500);
        });
      } else if (messagesContainerRef.current) {
        // No highlight — scroll to bottom (most recent messages)
        hasScrolledRef.current = true;
        requestAnimationFrame(() => {
          messagesContainerRef.current!.scrollTop = messagesContainerRef.current!.scrollHeight;
          setTimeout(() => {
            initialScrollDone.current = true;
          }, 500);
        });
      }
    }
  }, [loading, messages, effHighlightTimestamp, effActiveDateRange]);

  // Synchronized scrolling: observe which messages are visible in the right panel
  useEffect(() => {
    if (!messagesContainerRef.current || messages.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (isSyncScrolling.current) return; // Prevent loop
        // Find the topmost visible message
        let topMost: { id: string; top: number } | null = null;
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute("data-message-id");
            if (id && (!topMost || entry.boundingClientRect.top < topMost.top)) {
              topMost = { id, top: entry.boundingClientRect.top };
            }
          }
        }
        if (topMost && initialScrollDone.current) {
          setActiveMessageId(topMost.id);
        }
      },
      {
        root: messagesContainerRef.current,
        threshold: 0.1,
        rootMargin: "0px 0px -80% 0px", // Trigger when entering top 20% of container
      }
    );

    // Observe all message elements
    for (const [, el] of messageRefs.current) {
      observer.observe(el);
    }

    return () => observer.disconnect();
  }, [messages]);

  // Handle timeline entry click — scroll messages panel to that message + highlight
  const handleTimelineEntryClick = useCallback((messageId: string) => {
    const el = messageRefs.current.get(messageId);
    if (el) {
      isSyncScrolling.current = true;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setActiveMessageId(messageId);
      // Highlight the target message with amber ring
      setClickHighlightId(messageId);
      if (clickHighlightTimer.current) clearTimeout(clickHighlightTimer.current);
      clickHighlightTimer.current = setTimeout(() => setClickHighlightId(null), 2000);
      // Reset sync flag after scroll animation
      setTimeout(() => {
        isSyncScrolling.current = false;
      }, 600);
    }
  }, []);

  // Handle resync button click
  const handleResync = async () => {
    if (!effConversationId) return;
    setResyncSuccess(null);

    const result = await resync(effConversationId);

    if (result.success) {
      // Drop the neighbor-preview cache for this conversation so a stale card
      // can't reappear after a resync pulls new messages (F1 invalidate).
      if (organizationId) invalidatePreviewBundle(effConversationId, organizationId);
      // Refresh messages from database
      await fetchMessages();

      // Show simple success/no-op message
      const synced = result.messages_synced || 0;
      setResyncSuccess(synced > 0 ? "Synced" : "No new messages");

      // Clear success message after 3 seconds
      setTimeout(() => setResyncSuccess(null), 3000);
    }
  };

  // Handle ESC key to close.
  // CAPTURE phase + stopImmediatePropagation so that when this modal is stacked
  // on top of another modal (e.g. SetterProofModal → ConversationViewModal drill-down),
  // Escape closes THIS modal (the topmost) without also closing the underlying one.
  // Both modals listen on `window`; capture-phase runs before bubble-phase, and
  // stopImmediatePropagation prevents the underlying modal's same-target listener
  // from receiving the event.
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener("keydown", handleEsc, { capture: true });
      return () => window.removeEventListener("keydown", handleEsc, { capture: true });
    }
  }, [isOpen, onClose]);

  // ===========================================================================
  // F2-F7 nav engine — active only when navEnabled. Every hook below is called
  // unconditionally (null/undefined args when nav off) to keep hook order stable.
  // ===========================================================================
  const orgKey = organizationId ?? "";

  // Capture the conversation the modal opened on (deep-link scope, see above).
  useEffect(() => {
    if (isOpen) {
      if (openConvIdRef.current === null) openConvIdRef.current = effConversationId ?? null;
    } else {
      openConvIdRef.current = null;
    }
  }, [isOpen, effConversationId]);

  // Direction-aware navigation. Clamps at both ends (the key path clamps too, not
  // just the chevron-disable). The parent owns siblings+index; we only ask.
  const [slideDir, setSlideDir] = useState<-1 | 0 | 1>(0);
  const go = useCallback(
    (delta: number) => {
      if (!navEnabled || currentIndex == null || !onNavigate || !siblings) return;
      const next = currentIndex + delta;
      if (next < 0 || next > siblings.length - 1) return; // clamp at both ends
      setSlideDir(delta > 0 ? 1 : -1);
      onNavigate(next);
    },
    [navEnabled, currentIndex, onNavigate, siblings]
  );

  // Keyboard ArrowLeft/Right — capture-phase + stopImmediatePropagation, mirrors
  // the ESC handler so a stacked reader (over SetterProofModal) does not leak the
  // key to the modal beneath. Ignored while typing in a field.
  useEffect(() => {
    if (!isOpen || !navEnabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      const el = document.activeElement as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable))
        return;
      e.stopImmediatePropagation();
      e.preventDefault();
      go(e.key === "ArrowRight" ? 1 : -1);
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [isOpen, navEnabled, go]);

  // F5 — approach the loaded end of a paginated list → ask the parent for more.
  // A useRef latch guards a single in-flight onNeedMore; released when the list
  // grows (parent appended a page).
  const needMoreLatchRef = useRef(false);
  useEffect(() => {
    if (!navEnabled || currentIndex == null || !siblingsHasMore || !onNeedMore || !siblings) return;
    if (currentIndex >= siblings.length - 2 && !needMoreLatchRef.current) {
      needMoreLatchRef.current = true;
      onNeedMore();
    }
  }, [navEnabled, currentIndex, siblings, siblingsHasMore, onNeedMore]);
  useEffect(() => {
    needMoreLatchRef.current = false;
  }, [siblings?.length]);

  // Slide the reader on index change — direction-aware, reduced-motion aware.
  const [slideStyle, setSlideStyle] = useState<React.CSSProperties>({});
  useEffect(() => {
    if (!navEnabled || slideDir === 0) return;
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setSlideStyle({});
      return;
    }
    setSlideStyle({ transform: `translateX(${slideDir * 44}px)`, opacity: 0.35 });
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        setSlideStyle({
          transform: "translateX(0)",
          opacity: 1,
          transition: "transform 300ms cubic-bezier(.22,.61,.36,1), opacity 260ms ease-out",
        })
      )
    );
    return () => cancelAnimationFrame(raf);
  }, [slideDir, effConversationId, navEnabled]);

  // Drag on the message pane — threshold 84px, commit only when |dx| > |dy| so a
  // horizontal swipe navigates while vertical stays a scroll (touch-action pan-y).
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const onPanePointerDown = (e: React.PointerEvent) => {
    if (!navEnabled) return;
    dragStartRef.current = { x: e.clientX, y: e.clientY };
  };
  const onPanePointerUp = (e: React.PointerEvent) => {
    if (!navEnabled || !dragStartRef.current) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    dragStartRef.current = null;
    if (Math.abs(dx) > 84 && Math.abs(dx) > Math.abs(dy)) {
      go(dx < 0 ? 1 : -1); // drag left → next, drag right → prev
    }
  };

  // F4 prefetch + the two flanking cards' reactive data.
  useNeighborPrefetch(
    navEnabled ? siblings : undefined,
    navEnabled ? currentIndex : undefined,
    orgKey
  );
  const prevData = usePreviewData(
    navEnabled && currentIndex! > 0 ? siblings![currentIndex! - 1] : null,
    orgKey
  );
  const nextData = usePreviewData(
    navEnabled && currentIndex! < siblings!.length - 1 ? siblings![currentIndex! + 1] : null,
    orgKey
  );

  // F7 — the sibling set is a snapshot at open. If the org changes while the
  // reader is open with nav on, the neighbor set is no longer what is being read;
  // force-close (the plan's safe default).
  const openOrgIdRef = useRef<string | null>(null);
  const currentOrgId = dataCtx?.currentOrganization?.id ?? null;
  useEffect(() => {
    if (!isOpen) {
      openOrgIdRef.current = null;
      return;
    }
    if (openOrgIdRef.current === null) {
      openOrgIdRef.current = currentOrgId;
    } else if (navEnabled && currentOrgId !== openOrgIdRef.current) {
      onClose();
    }
  }, [isOpen, navEnabled, currentOrgId, onClose]);

  if (!mounted) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      {/* Backdrop */}
      <div
        className={`${closing ? "anim-backdrop-out" : "anim-backdrop-in"} fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity`}
        onClick={onClose}
      />

      {/* Modal — wider to accommodate timeline sidebar.
          anim-modal-in lives on this NON-glass wrapper — transform directly on the
          detail-overlay panel would force a backdrop-filter repaint (text blur).
          onClick closes on a click that lands on the wrapper itself (the empty area
          around the card), NOT on the card or its children — this wrapper sits on top
          of the backdrop and would otherwise swallow every outside click. */}
      <div
        className={`${closing ? "anim-modal-out" : "anim-modal-in"} flex min-h-full items-center justify-center gap-2 sm:gap-3 p-4`}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        {/* F3 — prev chevron (always visible when nav on) */}
        {navEnabled && (
          <button
            type="button"
            data-testid="dm-nav-prev"
            aria-label="Previous conversation"
            disabled={currentIndex === 0}
            onClick={() => go(-1)}
            className="anim-tap flex-shrink-0 w-9 h-9 rounded-full detail-overlay flex items-center justify-center text-glass-secondary hover:text-ds-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        )}
        {/* F3 — left preview card */}
        {navEnabled && (
          <PreviewCard
            data={prevData}
            direction="prev"
            testId="dm-preview-prev"
            onClick={() => go(-1)}
          />
        )}

        <div
          style={navEnabled ? slideStyle : undefined}
          className={navEnabled ? "w-full max-w-5xl min-w-0" : "contents"}
        >
          <div className="relative w-full max-w-5xl detail-overlay rounded-xl shadow-xl transform transition-all max-h-[95vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between gap-x-3 gap-y-2 p-4 border-b border-black/[0.08] flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 flex items-center justify-center text-glass font-bold flex-shrink-0">
                  {effContactName.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-glass truncate">{effContactName}</p>
                  {effLeadInstagram && (
                    <p className="token-11 text-glass-muted truncate">@{effLeadInstagram}</p>
                  )}
                </div>
                {/* Nav position indicator (F3) */}
                {navEnabled && (
                  <span
                    data-testid="dm-nav-position"
                    className="ml-1 token-11 font-mono text-glass-muted whitespace-nowrap flex-shrink-0"
                  >
                    {currentIndex! + 1} / {siblingsTotal ?? siblings!.length}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {/* Reply Timeline toggle — only below lg: (sidebar at line ~613 covers
                  lg: and up). Opens the same ReplyTimeline as an in-modal overlay. */}
                <button
                  type="button"
                  onClick={() => setShowTimelineOverlay(true)}
                  className="lg:hidden anim-tap px-2 py-1 token-11 font-medium rounded bg-black/[0.05] text-glass-secondary hover:bg-ds-accent/15 hover:text-ds-accent transition-colors"
                  title="Show reply timeline"
                  data-testid="reply-timeline-toggle"
                >
                  Timeline
                </button>
                <span className="token-14 text-glass-muted">
                  {messages.length} messages
                  {effActiveDateRange && (
                    <span className="ml-1 text-ds-accent">
                      &middot;{" "}
                      {effActiveDateRange.start.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                      {effActiveDateRange.start.toDateString() !==
                        effActiveDateRange.end.toDateString() &&
                        ` – ${effActiveDateRange.end.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`}
                    </span>
                  )}
                </span>

                {/* Dismiss Button — F6: with nav on, advance and keep the reader open
                  (the parent drops the row + re-threads siblings); with nav off,
                  keep today's close-on-dismiss behaviour. */}
                {onDismiss && effConversationId && (
                  <button
                    data-testid="dm-dismiss"
                    onClick={() => {
                      const id = effConversationId;
                      onDismiss(id);
                      if (organizationId) invalidatePreviewBundle(id, organizationId);
                      if (navEnabled && currentIndex != null && siblings && onNavigate) {
                        if (siblings.length <= 1) {
                          onClose(); // last row — nothing left to read
                        } else {
                          // Parent removes the current row, so the item that was next
                          // now sits at currentIndex; only clamp when we were at the end.
                          const wasLast = currentIndex >= siblings.length - 1;
                          onNavigate(wasLast ? currentIndex - 1 : currentIndex);
                        }
                      } else {
                        onClose();
                      }
                    }}
                    className="anim-tap px-2 py-1 token-11 font-medium rounded bg-black/[0.05] text-glass-secondary hover:bg-ds-red/20 hover:text-ds-red transition-colors"
                    title="Dismiss conversation"
                  >
                    Dismiss
                  </button>
                )}

                {/* Copy Conversation Button */}
                <CopyConversationButton
                  getText={() =>
                    serializeConversation({
                      messages,
                      contactName: effContactName,
                      leadInstagram: effLeadInstagram,
                      timezone,
                      orgName,
                    })
                  }
                  variant="header"
                  disabled={messages.length === 0}
                />

                {/* Resync Button */}
                <button
                  onClick={handleResync}
                  disabled={isResyncing}
                  className={`anim-tap px-2 py-1 token-11 font-medium rounded transition-colors ${
                    isResyncing
                      ? "bg-black/[0.05] text-glass-muted cursor-not-allowed"
                      : resyncSuccess
                        ? "bg-ds-green/20 text-ds-green"
                        : resyncError
                          ? "bg-ds-red/20 text-ds-red"
                          : "bg-black/[0.05] text-glass-secondary hover:bg-ds-accent/20 hover:text-ds-accent"
                  }`}
                  title="Fetch all messages from GHL"
                >
                  {isResyncing ? (
                    <span className="flex items-center gap-1">
                      <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      Syncing...
                    </span>
                  ) : resyncSuccess ? (
                    <span className="flex items-center gap-1">
                      <span>✓</span>
                      {resyncSuccess}
                    </span>
                  ) : resyncError ? (
                    <span className="flex items-center gap-1">
                      <span>⚠</span>
                      Failed
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <span>↻</span>
                      Resync
                    </span>
                  )}
                </button>

                <button
                  onClick={onClose}
                  className="anim-tap p-2 hover:bg-black/[0.05] rounded-full transition-colors"
                  aria-label="Close modal"
                >
                  <svg
                    className="w-5 h-5 text-glass-muted"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content — split layout: timeline | messages */}
            <div className="relative flex flex-1 overflow-hidden min-h-0">
              {/* Reply Timeline sidebar (hidden on small screens) */}
              <div className="hidden lg:flex w-[250px] flex-shrink-0 border-r border-black/[0.08] flex-col">
                <ReplyTimeline
                  messages={messages}
                  timezone={timezone}
                  setterName={setterName}
                  contactName={effContactName}
                  activeDateRange={effActiveDateRange}
                  onEntryClick={handleTimelineEntryClick}
                  activeMessageId={activeMessageId}
                  lifetimeReplyTurns={replyTurns}
                />
              </div>

              {/* Reply Timeline overlay (below lg: only) — full-width substitute for the
                hidden sidebar above. Covers just the content row (header + footer stay
                reachable). detail-overlay per /css: this is an overlay surface, not a
                glass card. */}
              {showTimelineOverlay && (
                <div
                  data-testid="reply-timeline-overlay"
                  className="lg:hidden absolute inset-0 z-20 detail-overlay flex flex-col"
                >
                  <div className="flex items-center justify-between px-4 py-3 border-b border-black/[0.08] flex-shrink-0">
                    <p className="token-13 font-semibold text-glass uppercase tracking-wide">
                      Reply Timeline
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowTimelineOverlay(false)}
                      className="anim-tap p-2 hover:bg-black/[0.05] rounded-full transition-colors"
                      aria-label="Close reply timeline"
                    >
                      <svg
                        className="w-5 h-5 text-glass-muted"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                  {/* No overflow-y-auto here — ReplyTimeline owns its own internal
                    scroll region (its entries list is flex-1 overflow-y-auto inside
                    its own h-full root); this wrapper just needs a bounded height,
                    same contract as the desktop sidebar wrapper above. */}
                  <div className="flex-1 min-h-0">
                    <ReplyTimeline
                      messages={messages}
                      timezone={timezone}
                      setterName={setterName}
                      contactName={effContactName}
                      activeDateRange={effActiveDateRange}
                      onEntryClick={(messageId) => {
                        handleTimelineEntryClick(messageId);
                        setShowTimelineOverlay(false);
                      }}
                      activeMessageId={activeMessageId}
                      lifetimeReplyTurns={replyTurns}
                    />
                  </div>
                </div>
              )}

              {/* Messages panel */}
              <div
                ref={messagesContainerRef}
                data-testid="dm-message-list"
                className="relative flex-1 p-4 overflow-y-auto bg-black/[0.03]"
                style={{
                  overscrollBehavior: "contain",
                  touchAction: navEnabled ? "pan-y" : undefined,
                }}
                onPointerDown={onPanePointerDown}
                onPointerUp={onPanePointerUp}
              >
                {/* Load older messages — cursor pagination (Phase 7.4) */}
                {hasOlderMessages && (
                  <div className="flex justify-center mb-3">
                    <button
                      onClick={loadOlderMessages}
                      disabled={loadingOlder}
                      className="anim-tap px-3 py-1.5 token-12 font-medium rounded-full bg-black/[0.05] text-glass-secondary hover:bg-ds-accent/10 hover:text-ds-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                    >
                      {loadingOlder ? (
                        <>
                          <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                          Loading...
                        </>
                      ) : (
                        <>↑ Load older messages</>
                      )}
                    </button>
                  </div>
                )}

                {/* Syncing overlay */}
                {isResyncing && (
                  <div className="sticky top-0 z-10 flex items-center justify-center gap-2 py-2 px-3 mb-3 bg-ds-accent border border-ds-accent/30 rounded-lg token-14 text-white animate-pulse">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Fetching messages from GHL...
                  </div>
                )}

                {error && (
                  <div className="p-4 bg-ds-red/10 border border-ds-red/30 text-ds-red rounded-lg mb-4">
                    Error: {error}
                  </div>
                )}

                {resyncError && (
                  <div className="p-3 bg-ds-red/10 border border-ds-red/30 text-ds-red rounded-lg mb-4 token-14">
                    Resync failed: {resyncError}
                  </div>
                )}

                {loading && !isResyncing && (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                  </div>
                )}

                {!loading && !isResyncing && messages.length === 0 && (
                  <p className="text-center text-glass-muted py-8">
                    No messages found for this conversation.
                  </p>
                )}

                {!loading &&
                  (() => {
                    let foundFirstInRange = false;
                    return messages.map((msg) => {
                      const highlightType = getHighlightType(
                        msg,
                        effHighlightTimestamp,
                        effAnchorTimestamp,
                        messages
                      );

                      // Check if message is outside the active date range
                      let dimmed = false;
                      let isFirstInRange = false;
                      if (effActiveDateRange) {
                        const msgTime = new Date(msg.sent_at).getTime();
                        const inRange =
                          msgTime >= effActiveDateRange.start.getTime() &&
                          msgTime <= effActiveDateRange.end.getTime();
                        dimmed = !inRange;
                        if (inRange && !foundFirstInRange) {
                          foundFirstInRange = true;
                          isFirstInRange = true;
                        }
                      }

                      // Ref priority: highlight > first-in-range > message ref for sync
                      const ref =
                        highlightType === "highlight"
                          ? highlightRef
                          : isFirstInRange
                            ? firstInRangeRef
                            : undefined;

                      return (
                        <div
                          key={msg.id}
                          data-message-id={msg.id}
                          data-testid={dimmed ? "dm-msg-dimmed" : undefined}
                          ref={(el) => {
                            // Set the message ref for synchronized scrolling
                            setMessageRef(msg.id, el);
                            // Also set highlight/firstInRange ref if applicable
                            if (ref && el) {
                              (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
                            }
                          }}
                        >
                          <ChatBubble
                            message={{
                              text: msg.message_text,
                              sender: msg.sender_type,
                              sent_at: msg.sent_at,
                              has_media: msg.has_media,
                            }}
                            timezone={timezone}
                            highlightType={
                              highlightType ||
                              (clickHighlightId === msg.id ? "highlight" : undefined)
                            }
                            dimmed={dimmed}
                            ownerName={
                              ghlUserNames[msg.owner_at_send ?? assignedUserId ?? ""] ?? null
                            }
                          />
                        </div>
                      );
                    });
                  })()}
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-black/[0.08] flex items-center justify-between">
              {ghlContactId && ghlLocationId ? (
                <a
                  href={`https://app.gohighlevel.com/v2/location/${ghlLocationId}/contacts/detail/${ghlContactId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="token-11 text-ds-accent font-mono hover:underline"
                >
                  {ghlConversationId || effConversationId}
                </a>
              ) : (
                <p className="token-11 text-glass-muted font-mono">
                  {ghlConversationId || effConversationId}
                </p>
              )}
              {setterName && <p className="token-11 text-glass-muted">Setter: {setterName}</p>}
            </div>
          </div>
        </div>
        {/* F3 — right preview card */}
        {navEnabled && (
          <PreviewCard
            data={nextData}
            direction="next"
            testId="dm-preview-next"
            onClick={() => go(1)}
          />
        )}
        {/* F3 — next chevron */}
        {navEnabled && (
          <button
            type="button"
            data-testid="dm-nav-next"
            aria-label="Next conversation"
            disabled={currentIndex === siblings!.length - 1}
            onClick={() => go(1)}
            className="anim-tap flex-shrink-0 w-9 h-9 rounded-full detail-overlay flex items-center justify-center text-glass-secondary hover:text-ds-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>
    </div>,
    document.body
  );
};
